import { memo } from "react";
import { FolderGitIcon } from "lucide-react";

import type { OpenLocalHandoffOutcome } from "~/openLocalHandoff";
import type { OpenLocalMatch } from "@cmmd-forge/shared/openLocalTarget";
import { Button } from "../ui/button";
import {
  Dialog,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogPopup,
  DialogTitle,
} from "../ui/dialog";

/**
 * What the user sees after clicking `Open locally` on a finished cloud task.
 *
 * ## Why this confirms instead of just doing it
 *
 * The click already expressed intent, so a confirmation step looks redundant.
 * It is not: acting on this runs a checkout in a working copy that may have
 * uncommitted work in it, and the user cannot see from the cloud UI which local
 * directory that will be. Naming the directory before touching it is the whole
 * point of the step.
 *
 * ## Why the ambiguous case is a list and not a best guess
 *
 * Forge creates worktrees routinely and they all report the same git remote, so
 * "which checkout is `owner/repo`?" honestly has several answers. Ranking them
 * would be inventing a preference the link never expressed, so every candidate
 * is shown by path — the one thing that actually distinguishes them.
 */

interface OpenLocalHandoffDialogProps {
  readonly outcome: OpenLocalHandoffOutcome | null;
  readonly onDismiss: () => void;
  readonly onOpen: (match: OpenLocalMatch) => void;
}

function BranchLine({ repoFullName, branch }: { repoFullName: string; branch: string }) {
  return (
    <span className="font-mono text-xs">
      {repoFullName}
      <span className="text-muted-foreground"> · </span>
      {branch}
    </span>
  );
}

/** Copy per outcome, kept out of the JSX so each case reads as its own sentence. */
function describeOutcome(outcome: OpenLocalHandoffOutcome) {
  const { repoFullName, branch } = outcome.request;
  const ref = <BranchLine repoFullName={repoFullName} branch={branch} />;

  switch (outcome.kind) {
    case "no-match":
      return {
        title: "No local copy of this repository",
        description: (
          <>
            Nothing on this machine is checked out from {ref}. Add the project to Forge first, then
            try the link again.
          </>
        ),
      };
    case "ambiguous":
      return {
        title: "Open locally",
        description: (
          <>More than one project here is checked out from {ref}. Choose which one to switch.</>
        ),
      };
    default:
      return {
        title: "Open locally",
        description: <>Switch to {ref} in the project below.</>,
      };
  }
}

/** The checkouts offered for this outcome — none when nothing matched. */
function candidatesOf(outcome: OpenLocalHandoffOutcome): readonly OpenLocalMatch[] {
  if (outcome.kind === "ambiguous") return outcome.candidates;
  if (outcome.kind === "open") return [outcome.match];
  return [];
}

export const OpenLocalHandoffDialog = memo(function OpenLocalHandoffDialog({
  outcome,
  onDismiss,
  onOpen,
}: OpenLocalHandoffDialogProps) {
  if (!outcome) return null;

  const { title, description } = describeOutcome(outcome);
  const candidates = candidatesOf(outcome);

  return (
    <Dialog open onOpenChange={(next) => !next && onDismiss()}>
      <DialogPopup showCloseButton>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {candidates.length > 0 && (
          <div className="flex flex-col gap-2 px-6 py-2">
            {candidates.map((candidate) => (
              <button
                key={candidate.projectId}
                type="button"
                onClick={() => onOpen(candidate)}
                className="flex items-center gap-2 rounded-lg border border-border bg-transparent px-3 py-2.5 text-left text-sm text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
              >
                <FolderGitIcon className="size-4 shrink-0" />
                <span className="truncate font-mono text-xs">{candidate.workspaceRoot}</span>
              </button>
            ))}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={onDismiss}>
            {outcome.kind === "no-match" ? "Close" : "Cancel"}
          </Button>
        </DialogFooter>
      </DialogPopup>
    </Dialog>
  );
});
